import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Appointment, AppointmentListItem, BASE_URL_TOKEN, CommunicationPreference, SearchCriteria } from '@raptor-fe/core';
import { LoadingHeaders } from '../web-loading-service/web-loading-headers';

export interface CompleteCheckInRequest {
    signatureImageUrl?: string;
    advisorUserId: string;
    acceptsServiceTerms: boolean;
    agreementId?: string;
    /**
     * Bound to the static marketing-consent checkbox on the Terms card. Written to the
     * customer's Raptor DB marketing consent columns by the backend when it differs from
     * what is stored. Null when the advisor left the checkbox untouched, or when a
     * federation preference supersedes it; null leaves the stored consent unchanged.
     * Independent of `communicationPreferences`: this consent drives Raptor DB and
     * downstream Raptor-owned marketing paths; the preferences list below drives
     * external federation-owned channels.
     */
    marketingConsent: boolean | null;
    selectedAddOnIds?: string[];
    /**
     * Federation-driven per-channel consent list. Omitted when the federation offered
     * no channels (backend treats absence as "no federation prefs to push"); present
     * when the checkbox list rendered. Backend forwards this to the external system
     * and does not persist it in Raptor DB.
     */
    communicationPreferences?: CommunicationPreference[];
    /**
     * The customer's concerns as typed by the advisor at check-in. Stored as a timestamped
     * customer note and composed onto the federation's own booking note, which is why it
     * travels on the submit rather than the appointment update.
     */
    customerNote?: string;
    /**
     * True when the advisor signs in place of a customer who is not present to sign, so the
     * signature in `signatureImageUrl` is the advisor's. The server treats absence as false.
     */
    signedOnBehalfOfCustomer?: boolean;
}

@Injectable({
    providedIn: 'root',
})
export class AppointmentApiService {
    private readonly http = inject(HttpClient);
    private readonly baseUrl = inject(BASE_URL_TOKEN);

    get(tenantId: string, branchId: string, id: string): Observable<Appointment> {
        return this.http.get<Appointment>(this.getUrl(tenantId, branchId, id));
    }

    getAppointmentsByDate(tenantId: string, branchId: string, date: string, showLoading = true): Observable<Appointment[]> {
        const params = this.dateRangeParams(date);
        return this.http.get<Appointment[]>(this.getUrl(tenantId, branchId, 'date'), {
            params,
            headers: showLoading ? undefined : LoadingHeaders.httpHeadersWithNoLoading(),
        });
    }

    searchAppointmentsByKeyword(tenantId: string, branchId: string, date: string, keyword: string): Observable<Appointment[]> {
        const params = this.dateRangeParams(date);
        return this.http.get<Appointment[]>(this.getUrl(tenantId, branchId, `search/keyword/${keyword}`), { params });
    }

    create(tenantId: string, branchId: string, appointment: Appointment): Observable<Appointment> {
        return this.http.post<Appointment>(this.getUrl(tenantId, branchId), appointment);
    }

    update(tenantId: string, branchId: string, appointment: Appointment): Observable<Appointment> {
        return this.http.put<Appointment>(this.getUrl(tenantId, branchId), appointment);
    }

    completeCheckIn(tenantId: string, branchId: string, appointmentId: string, request: CompleteCheckInRequest): Observable<Appointment> {
        return this.http.post<Appointment>(this.getUrl(tenantId, branchId, `${appointmentId}/complete-checkin`), request);
    }

    /**
     * Fetches the federation-driven list of communication preferences for the customer
     * associated with the given appointment. Called at check-in flow init so the terms
     * step can render one checkbox per federation-known channel with the current consent.
     *
     * No Raptor DB persistence -- the federation is the source of truth. Empty list means
     * the federation has no channels to offer (or is unreachable at the backend layer);
     * callers must handle empty gracefully by hiding the dynamic preferences below the
     * static marketing checkbox. The static marketing checkbox stays visible either way --
     * it is a separate Raptor-owned consent, not a fallback for this list.
     */
    getCommunicationPreferences(tenantId: string, branchId: string, appointmentId: string): Observable<CommunicationPreference[]> {
        return this.http.get<CommunicationPreference[]>(this.getUrl(tenantId, branchId, `${appointmentId}/communication-preferences`));
    }

    delete(tenantId: string, branchId: string, id: string): Observable<object> {
        return this.http.delete(this.getUrl(tenantId, branchId, id));
    }

    /**
     * Lists completed-and-signed appointments for the "Completed Appointments" admin/auto-login
     * surface. The BE filters to `Status == Completed AND SignatureImageUrl != null` (eligibility
     * invariant, server-enforced). The supplied {@link SearchCriteria} carries pagination,
     * OrderBy, and all predicate items: a `checkedindatetime` window plus an optional `Like`
     * item on `customer.name`, `vehicle.licenseplate`, or `workorder.number`. Infinite-scroll
     * consumers call again with `pageNumber + 1` until the array length is less than `pageSize`.
     */
    searchCompletedSigned(tenantId: string, branchId: string, searchCriteria: SearchCriteria): Observable<AppointmentListItem[]> {
        return this.http.post<AppointmentListItem[]>(this.getUrl(tenantId, branchId, 'search'), searchCriteria);
    }

    private dateRangeParams(date: string): HttpParams {
        const [year, month, day] = date.split('-').map(Number);
        const start = new Date(year, month - 1, day, 0, 0, 0);
        const end = new Date(year, month - 1, day + 1, 0, 0, 0);
        return new HttpParams().set('start', start.toISOString()).set('end', end.toISOString());
    }

    private getUrl(tenantId: string, branchId: string, path = ''): string {
        const base = `${this.baseUrl}v1/tenant/${tenantId}/branch/${branchId}/appointment`;
        return path ? `${base}/${path}` : base;
    }
}
